import {App} from '@capacitor/app';
import {Browser} from '@capacitor/browser';
import {Capacitor} from '@capacitor/core';
import {Keyboard, KeyboardResize} from '@capacitor/keyboard';
import {Network} from '@capacitor/network';
import {SplashScreen} from '@capacitor/splash-screen';
import {StatusBar, Style} from '@capacitor/status-bar';

export function isNativePlatform() {
  return Capacitor.isNativePlatform();
}

export function nativePlatform() {
  return Capacitor.getPlatform();
}

export async function initializeNativeShell() {
  if (!isNativePlatform()) return null;
  document.documentElement.classList.add('native-app');
  document.body.classList.add('native-app');

  await Promise.allSettled([
    StatusBar.setStyle({style: Style.Dark}),
    StatusBar.setBackgroundColor({color: '#0f0b1c'}),
    Keyboard.setResizeMode({mode: KeyboardResize.Native}),
  ]);

  const status = await Network.getStatus();
  document.body.classList.toggle('native-offline', !status.connected);
  await Network.addListener('networkStatusChange', state => {
    document.body.classList.toggle('native-offline', !state.connected);
    window.dispatchEvent(new CustomEvent('native:network', {detail: state}));
  });
  await Keyboard.addListener('keyboardWillShow', () =>
    document.body.classList.add('native-keyboard-open'),
  );
  await Keyboard.addListener('keyboardWillHide', () =>
    document.body.classList.remove('native-keyboard-open'),
  );
  await App.addListener('appStateChange', ({isActive}) => {
    if (isActive) window.dispatchEvent(new CustomEvent('native:resume'));
  });
  await App.addListener('backButton', () => {
    if (window.history.length > 1) window.history.back();
    else void App.exitApp();
  });
  await App.addListener('appUrlOpen', ({url}) => openDeepLink(url));
  const launchUrl = await App.getLaunchUrl();
  const launchRoute = launchUrl?.url
    ? authorizedRouteFromDeepLink(launchUrl.url)
    : null;

  document.addEventListener('click', event => {
    const anchor = (event.target as HTMLElement).closest('a');
    if (!anchor?.href) return;
    const target = new URL(anchor.href);
    const apiOrigin = new URL(requiredNativeApiOrigin());
    if (
      target.protocol === 'https:' &&
      target.host === window.location.host
    ) {
      return;
    }
    if (target.protocol === 'https:' && target.host === apiOrigin.host) {
      event.preventDefault();
      navigateWithinApp(`${target.pathname}${target.search}${target.hash}`);
      return;
    }
    if (target.protocol === 'http:' || target.protocol === 'https:') {
      event.preventDefault();
      void Browser.open({url: target.href});
    }
  });

  await SplashScreen.hide();
  return launchRoute;
}

function openDeepLink(url: string) {
  const route = authorizedRouteFromDeepLink(url);
  if (route) navigateWithinApp(route);
}

export function requiredNativeApiOrigin() {
  const origin = import.meta.env.VITE_NATIVE_API_ORIGIN as string | undefined;
  if (!origin || !/^https:\/\//.test(origin)) {
    throw new Error('VITE_NATIVE_API_ORIGIN must be an HTTPS origin.');
  }
  return origin.replace(/\/+$/, '');
}

function authorizedRouteFromDeepLink(value: string): string | null {
  try {
    const url = new URL(value);
    const allowedHost = new URL(requiredNativeApiOrigin()).host;
    const configuredScheme = import.meta.env
      .VITE_CAPACITOR_URL_SCHEME as string | undefined;
    if (
      url.protocol === 'https:' &&
      url.host === allowedHost
    ) {
      return `${url.pathname}${url.search}${url.hash}`;
    }
    if (configuredScheme && url.protocol === `${configuredScheme}:`) {
      return `/${url.host}${url.pathname}${url.search}${url.hash}`;
    }
  } catch {
    return null;
  }
  return null;
}

export function navigateWithinApp(route: string) {
  if (!route.startsWith('/') || route.startsWith('//')) return;
  window.history.pushState({}, '', route);
  window.dispatchEvent(new PopStateEvent('popstate'));
}
